import { differenceInSeconds, format, formatDistanceToNowStrict, isValid, parseISO } from 'date-fns';

const toDate = (value: string | number | Date | undefined | null): Date | null => {
  if (value === undefined || value === null) return null;
  const date = value instanceof Date ? value : typeof value === 'number' ? new Date(value) : parseISO(value);
  return isValid(date) ? date : null;
};

/** "12s ago" / "4m ago" — compact enough for a table cell. */
export function relativeTime(value?: string | number | Date | null): string {
  const date = toDate(value);
  if (!date) return '—';
  const seconds = Math.abs(differenceInSeconds(new Date(), date));
  if (seconds < 10) return 'just now';
  if (seconds < 60) return `${seconds}s ago`;
  return `${formatDistanceToNowStrict(date, { roundingMethod: 'floor' })
    .replace(' minutes', 'm')
    .replace(' minute', 'm')
    .replace(' hours', 'h')
    .replace(' hour', 'h')
    .replace(' days', 'd')
    .replace(' day', 'd')
    .replace(' months', 'mo')
    .replace(' month', 'mo')
    .replace(' years', 'y')
    .replace(' year', 'y')} ago`;
}

export function absoluteTime(value?: string | number | Date | null, pattern = 'd MMM yyyy, h:mm a'): string {
  const date = toDate(value);
  return date ? format(date, pattern) : '—';
}

export function clockTime(value?: string | number | Date | null): string {
  const date = toDate(value);
  return date ? format(date, 'h:mm:ss a') : '—';
}

export function dayLabel(value?: string | number | Date | null): string {
  const date = toDate(value);
  return date ? format(date, 'EEE d MMM') : '—';
}

/** 4 261 s → "1h 11m". Zero-safe; never renders "0h 0m". */
export function duration(seconds?: number | null): string {
  if (!seconds || seconds < 0) return '0m';
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = Math.floor(seconds % 60);
  if (h > 0) return m > 0 ? `${h}h ${m}m` : `${h}h`;
  if (m > 0) return `${m}m`;
  return `${s}s`;
}

const number = (value: number, digits: number) =>
  value.toLocaleString(undefined, { minimumFractionDigits: digits, maximumFractionDigits: digits });

export function km(value?: number | null, digits = 1): string {
  if (value === undefined || value === null) return '—';
  if (value < 1) return `${Math.round(value * 1000)} m`;
  return `${number(value, digits)} km`;
}

export function speed(value?: number | null): string {
  if (value === undefined || value === null) return '—';
  return `${Math.round(value)} km/h`;
}

/**
 * Data volume in the units a SIM plan is sold in — decimal MB/GB, not binary,
 * because that is how carriers bill.
 */
export function bytes(value?: number | null): string {
  if (value === undefined || value === null) return '—';
  if (value < 1_000) return `${Math.round(value)} B`;
  if (value < 1_000_000) return `${(value / 1_000).toFixed(1)} kB`;
  if (value < 1_000_000_000) return `${(value / 1_000_000).toFixed(1)} MB`;
  return `${(value / 1_000_000_000).toFixed(2)} GB`;
}

export function percent(value?: number | null): string {
  return value === undefined || value === null ? '—' : `${Math.round(value)}%`;
}

/**
 * Devices report signal on the GSM CSQ scale (0–31), not as a percentage.
 * Showing the raw value alongside a word keeps both audiences happy.
 */
export function signal(value?: number | null): string {
  if (value === undefined || value === null) return '—';
  const quality = value >= 20 ? 'good' : value >= 10 ? 'fair' : 'weak';
  return `${value}/31 · ${quality}`;
}

/**
 * Money is carried in minor units (pesewas) as an integer everywhere — the API,
 * the store and the wire — and only becomes a decimal here, at the last moment
 * before a human reads it.
 */
export function money(minor?: number | null, currency = 'GHS'): string {
  if (minor === undefined || minor === null) return '—';
  const major = minor / 100;
  return `${currency} ${major.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

/** Compact form for stat tiles: GHS 12.4k rather than GHS 12,400.00. */
export function moneyCompact(minor?: number | null, currency = 'GHS'): string {
  if (minor === undefined || minor === null) return '—';
  const major = minor / 100;
  if (Math.abs(major) < 10_000) return money(minor, currency);
  return `${currency} ${(major / 1000).toFixed(1)}k`;
}

export function coordinate(lat?: number | null, lng?: number | null): string {
  if (lat === undefined || lat === null || lng === undefined || lng === null) return '—';
  return `${lat.toFixed(5)}, ${lng.toFixed(5)}`;
}

export function initials(firstName?: string, lastName?: string, fallback = '?'): string {
  const value = `${firstName?.[0] ?? ''}${lastName?.[0] ?? ''}`.trim();
  return value ? value.toUpperCase() : fallback;
}

/** SPEEDING → Speeding, GEOFENCE_ENTRY → Geofence entry. */
export function humanizeEnum(value?: string | null): string {
  if (!value) return '—';
  const lower = value.toLowerCase().replace(/_/g, ' ');
  return lower.charAt(0).toUpperCase() + lower.slice(1);
}

export function fullName(user?: { firstName?: string; lastName?: string } | null): string {
  if (!user) return '—';
  return [user.firstName, user.lastName].filter(Boolean).join(' ') || '—';
}
